import { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import {
  useSubscription,
  ApolloError,
  type OperationVariables,
} from '@apollo/client'
import { emitGraphQLError } from './errors'
import {
  useIdleAndOnlineStatus,
  DEFAULT_IDLE_TIMEOUT_MS,
} from './use-idle-and-online-status'
import { useDeepCompareMemo } from './utils'
import type {
  UseGraphQLSubscriptionProps,
  GraphQLSubscriptionResult,
} from './types'
import type { OperationDefinitionNode } from 'graphql'

export const useGraphQLSubscription = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>({
  query,
  variables,
  skip = false,
  context,
  onData,
  onError,
  onComplete,
  autoPauseOnIdle = true,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
  backoffBaseMs = 1000,
  backoffMaxMs = 30000,
  backoffJitterRatio = 0.2,
  backoffMaxAttempts = 5,
}: UseGraphQLSubscriptionProps<
  TData,
  TVariables
>): GraphQLSubscriptionResult<TData> => {
  if (!query) throw new Error('useGraphQLSubscription: query is required')

  const stableVariables = useDeepCompareMemo(variables)
  const stableContext = useDeepCompareMemo(context)

  const queryDef = query.definitions.find(
    (def) => def.kind === 'OperationDefinition',
  ) as OperationDefinitionNode | undefined
  const queryName = queryDef?.name?.value
  const operationType = queryDef?.operation
  if (operationType !== 'subscription') {
    throw new Error(
      `useGraphQLSubscription: Expected a subscription operation, but received '${operationType}'.`,
    )
  }

  const subscribersRef = useRef<
    Set<
      (result: {
        data: TData | null
        loading: boolean
        error: ApolloError | null
      }) => void
    >
  >(new Set())
  const [restartSeq, setRestartSeq] = useState(0)
  const [isBackoffPause, setIsBackoffPause] = useState(false)
  const backoffAttemptRef = useRef(0)
  const backoffTimeoutRef = useRef<number | null>(null)

  const { isIdle, isOffline } = useIdleAndOnlineStatus(
    autoPauseOnIdle,
    idleTimeoutMs,
  )
  const wasPausedRef = useRef<boolean>(false)

  const effectiveExternalSkip = Boolean(skip || isIdle || isOffline)
  const effectiveSkip = Boolean(skip || isIdle || isOffline || isBackoffPause)

  const effectiveContext = useMemo(
    () => ({ ...(stableContext || {}), __subscriptionRestart__: restartSeq }),
    [stableContext, restartSeq],
  )

  useEffect(() => {
    if (!isOffline && wasPausedRef.current) {
      wasPausedRef.current = false
      backoffAttemptRef.current = 0
      if (backoffTimeoutRef.current) {
        window.clearTimeout(backoffTimeoutRef.current)
        backoffTimeoutRef.current = null
      }
      setIsBackoffPause(false)
      setRestartSeq((n) => n + 1)
    }
  }, [isOffline])

  useEffect(() => {
    if (effectiveExternalSkip) {
      wasPausedRef.current = true
    }
  }, [effectiveExternalSkip])

  const scheduleBackoffRestart = useCallback(() => {
    if (isOffline || isIdle) return
    if (
      backoffTimeoutRef.current ||
      backoffAttemptRef.current >= backoffMaxAttempts
    )
      return

    setIsBackoffPause(true)
    const attempt = (backoffAttemptRef.current += 1)
    const expDelay = Math.min(
      backoffMaxMs,
      backoffBaseMs * Math.pow(2, attempt - 1),
    )
    const jitter =
      1 + (Math.random() * 2 - 1) * Math.max(0, Math.min(1, backoffJitterRatio))
    const delay = Math.floor(expDelay * jitter)

    backoffTimeoutRef.current = window.setTimeout(() => {
      backoffTimeoutRef.current = null
      setIsBackoffPause(false)
      setRestartSeq((n) => n + 1)
    }, delay)
  }, [
    backoffBaseMs,
    backoffMaxMs,
    backoffJitterRatio,
    backoffMaxAttempts,
    isOffline,
    isIdle,
  ])

  const { data, loading, error } = useSubscription<TData, TVariables>(query, {
    variables: stableVariables,
    skip: effectiveSkip,
    context: effectiveContext,
    errorPolicy: 'all',
    ignoreResults: Boolean(onData),
    onData: ({ data: subscriptionResult }) => {
      const nextData = subscriptionResult?.data as TData | undefined
      if (onData) onData(nextData)

      backoffAttemptRef.current = 0
      if (backoffTimeoutRef.current) {
        window.clearTimeout(backoffTimeoutRef.current)
        backoffTimeoutRef.current = null
      }
      if (isBackoffPause) setIsBackoffPause(false)

      subscribersRef.current.forEach((listener) =>
        listener({
          data: nextData ?? null,
          loading: Boolean(subscriptionResult?.loading),
          error: null,
        }),
      )
    },
    onError: (err) => {
      if (onError) onError(err)
      subscribersRef.current.forEach((listener) =>
        listener({ data: null, loading: false, error: err }),
      )
      emitGraphQLError(err, { operation: 'subscription', queryName })

      if (!effectiveSkip) scheduleBackoffRestart()
    },
    onComplete: () => {
      if (onComplete) onComplete()
    },
  })

  const subscribe = useCallback(
    (
      callback: (result: {
        data: TData | null
        loading: boolean
        error: ApolloError | null
      }) => void,
    ) => {
      callback({ data: data || null, loading, error: error || null })
      subscribersRef.current.add(callback)
      return {
        unsubscribe: () => {
          subscribersRef.current.delete(callback)
        },
      }
    },
    [data, loading, error],
  )

  const getCurrentResult = useCallback(
    () => ({ data: data || null, loading, error: error || null }),
    [data, loading, error],
  )

  useEffect(() => {
    const currentSubscribers = subscribersRef.current
    return () => {
      if (backoffTimeoutRef.current) {
        window.clearTimeout(backoffTimeoutRef.current)
        backoffTimeoutRef.current = null
      }
      currentSubscribers.clear()
    }
  }, [])

  return useMemo(
    () => ({
      data: data || null,
      loading,
      error: error || null,
      subscribe,
      getCurrentResult,
    }),
    [data, loading, error, subscribe, getCurrentResult],
  )
}
